import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import {
  useServisKayitlari,
  createServisKaydi,
  updateServisKaydi,
  deleteServisKaydi,
  ISLEM_TURLERI,
  ODEME_TIPLERI,
  SERVIS_DURUMLARI,
  ODEME_DURUMLARI,
  ServisKaydi,
} from './hooks/useServisKayitlari';
import { useAuth } from '@/contexts/AuthContext';

const islemRenk: Record<string, string> = {
  'Filtre Bakımı – Dörtlü': 'bg-sky-100 text-sky-700',
  'Filtre Bakımı – Beşli': 'bg-indigo-100 text-indigo-700',
  'Genel Bakım': 'bg-emerald-100 text-emerald-700',
  'Yeni Montaj': 'bg-violet-100 text-violet-700',
  'Bina Girişi': 'bg-teal-100 text-teal-700',
  'Bina Girişi Bakım': 'bg-cyan-100 text-cyan-700',
  'Taşıma': 'bg-orange-100 text-orange-700',
  'Diğer': 'bg-gray-100 text-gray-700',
};

/* ─── Print Modal ─── */
function PrintModal({ record, firmaAdi, onClose }: { record: ServisKaydi; firmaAdi: string; onClose: () => void }) {
  const durum = SERVIS_DURUMLARI.find(d => d.value === record.servis_durumu);
  const odeme = ODEME_DURUMLARI.find(d => d.value === record.odeme_durumu);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-800">Servis Fişi</h3>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="px-3 py-1.5 bg-sky-600 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-sky-700">
              <i className="ri-printer-line mr-1"></i>Yazdır
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4" id="print-content">
          <div className="text-center border-b border-dashed border-gray-300 pb-4">
            <p className="font-bold text-lg">{firmaAdi}</p>
            <p className="text-xs text-gray-500">Servis Fişi</p>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Müşteri:</span><span className="font-semibold">{record.musteri_ad} {record.musteri_soyad}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Telefon:</span><span>{record.musteri_telefon || '-'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Adres:</span><span className="text-right max-w-[200px] text-xs">{record.musteri_adres || '-'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Tarih:</span><span>{record.servis_tarihi ? new Date(record.servis_tarihi).toLocaleDateString('tr-TR') : '-'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Saat:</span><span>{record.servis_saati || '-'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">İşlem:</span><span className="font-semibold">{record.islem_turu}</span></div>
            {record.personel_ad && <div className="flex justify-between"><span className="text-gray-500">Personel:</span><span>{record.personel_ad} {record.personel_soyad}</span></div>}
            <div className="flex justify-between"><span className="text-gray-500">Durum:</span><span className="font-semibold">{durum?.label || record.servis_durumu}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Ödeme Tipi:</span><span>{record.odeme_tipi}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Ödeme:</span><span className="font-semibold">{odeme?.label || record.odeme_durumu}</span></div>
            {record.servis_notu && <div className="flex justify-between"><span className="text-gray-500">Not:</span><span className="text-right max-w-[200px] text-xs">{record.servis_notu}</span></div>}
          </div>
          <div className="border-t-2 border-dashed border-gray-300 pt-4 flex justify-between items-center">
            <span className="font-bold text-gray-700">Toplam Ücret</span>
            <span className="text-2xl font-bold text-emerald-600">₺{record.alinan_ucret.toLocaleString('tr-TR')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Durum Güncelleme Modal ─── */
function DurumModal({ record, onClose, onSaved }: { record: ServisKaydi; onClose: () => void; onSaved: () => void }) {
  const [durum, setDurum] = useState(record.servis_durumu);
  const [aciklama, setAciklama] = useState(record.yapilan_islem_aciklamasi || '');
  const [ucret, setUcret] = useState(record.alinan_ucret ? String(record.alinan_ucret) : '');
  const [odemeTipi, setOdemeTipi] = useState(record.odeme_tipi || 'Nakit');
  const [odemeDurum, setOdemeDurum] = useState(record.odeme_durumu);
  const [saving, setSaving] = useState(false);
  const [firmaAyarlari, setFirmaAyarlari] = useState<{iban?: string}>();

  useEffect(() => {
    supabase.from('sadem_firma_ayarlari').select('iban').single().then(({ data }) => {
      if (data) setFirmaAyarlari(data);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const isBakimIslemi = [
        'Filtre Bakımı – Dörtlü',
        'Filtre Bakımı – Beşli',
        'Genel Bakım',
        'Bina Girişi Bakım',
      ].includes(record.islem_turu);

      const tamamlandiMi = durum === 'tamamlandi';

      await updateServisKaydi(record.id, {
        servis_durumu: durum,
        yapilan_islem_aciklamasi: aciklama,
        alinan_ucret: ucret ? Number(ucret) : 0,
        odeme_tipi: odemeTipi,
        odeme_durumu: ucret && Number(ucret) > 0 ? 'odendi' : odemeDurum,
      });

      // Bakım işlemi tamamlandıysa bakım takibini güncelle
      if (isBakimIslemi && tamamlandiMi) {
        const bugun = new Date().toISOString().split('T')[0];
        const altiAySonra = new Date();
        altiAySonra.setMonth(altiAySonra.getMonth() + 6);
        const altiAySonraStr = altiAySonra.toISOString().split('T')[0];

        // Mevcut aktif bakım kaydını kontrol et
        const { data: mevcutBakim } = await supabase
          .from('sadem_bakim_takibi')
          .select('id')
          .eq('musteri_id', record.musteri_id)
          .eq('aktif', true)
          .maybeSingle();

        if (mevcutBakim) {
          // Mevcut bakım kaydını güncelle
          await supabase
            .from('sadem_bakim_takibi')
            .update({
              son_bakim_tarihi: bugun,
              bir_sonraki_bakim_tarihi: altiAySonraStr,
              ilgili_servis_kaydi_id: record.id,
              updated_at: new Date().toISOString(),
            })
            .eq('id', mevcutBakim.id);
        } else {
          // Yeni bakım kaydı oluştur
          await supabase
            .from('sadem_bakim_takibi')
            .insert({
              musteri_id: record.musteri_id,
              son_bakim_tarihi: bugun,
              bakim_periyodu_ay: 6,
              bir_sonraki_bakim_tarihi: altiAySonraStr,
              ilgili_servis_kaydi_id: record.id,
              aktif: true,
            });
        }
      }

      onSaved();
    } catch (err: any) {
      alert('Güncelleme sırasında bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-200 bg-white';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-800">Servis Durumu Güncelle</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Servis Durumu</label>
            <div className="grid grid-cols-2 gap-2">
              {SERVIS_DURUMLARI.map(d => (
                <button key={d.value} type="button" onClick={() => setDurum(d.value)}
                  className={`text-xs font-semibold py-2.5 px-3 rounded-lg border transition-all cursor-pointer whitespace-nowrap
                    ${durum === d.value ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Yapılan İşlem Açıklaması</label>
            <textarea className={`${inputCls} resize-none`} rows={3} value={aciklama} onChange={e => setAciklama(e.target.value)} placeholder="Yapılan işlem detayları..." maxLength={500} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Alınan Ücret (₺)</label>
              <input type="number" min="0" className={inputCls} value={ucret} onChange={e => setUcret(e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ödeme Tipi</label>
              <select className={inputCls} value={odemeTipi} onChange={e => setOdemeTipi(e.target.value)}>
                {ODEME_TIPLERI.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {odemeTipi === 'IBAN' && firmaAyarlari.iban && (
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 font-medium">Firma IBAN</p>
              <p className="text-sm font-bold text-gray-800 font-mono select-all">{firmaAyarlari.iban}</p>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ödeme Durumu</label>
            <div className="flex gap-2">
              {ODEME_DURUMLARI.map(d => (
                <button key={d.value} type="button" onClick={() => setOdemeDurum(d.value)}
                  className={`flex-1 text-xs font-semibold py-2.5 rounded-lg border transition-all cursor-pointer whitespace-nowrap
                    ${odemeDurum === d.value ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap">İptal</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap transition-colors disabled:opacity-50">
              {saving ? 'Kaydediliyor...' : 'Güncelle'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function ServisKayitlariPage() {
  const { isAdmin, isOffice } = useAuth();
  const {
    services, loading, error, search, setSearch, filterDurum, setFilterDurum,
    page, setPage, totalPages, totalCount, durumCounts, refetch,
  } = useServisKayitlari();

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [printRecord, setPrintRecord] = useState<ServisKaydi | null>(null);
  const [durumRecord, setDurumRecord] = useState<ServisKaydi | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [firmaAdi, setFirmaAdi] = useState('Sadem Su Arıtma');

  // Form state
  const [musteriler, setMusteriler] = useState<any[]>([]);
  const [personeller, setPersoneller] = useState<any[]>([]);
  const [cihazlar, setCihazlar] = useState<any[]>([]);
  const [form, setForm] = useState({
    musteriId: '', cihazId: '', personelId: '',
    tarih: new Date().toISOString().split('T')[0],
    saat: '09:00',
    islemTuru: 'Filtre Bakımı – Dörtlü',
    digerAciklama: '',
    servisNotu: '',
    alinanUcret: '',
    odemeTipi: 'Nakit',
    birSonrakiBakim: '',
  });

  useEffect(() => {
    supabase.from('sadem_firma_ayarlari').select('firma_adi').single().then(({ data }) => {
      if (data?.firma_adi) setFirmaAdi(data.firma_adi);
    }).catch(() => {});
    supabase.from('sadem_musteriler').select('id, ad, soyad, telefon').eq('aktif', true).eq('silindi', false).order('ad').then(({ data }) => {
      if (data) setMusteriler(data);
    }).catch(() => {});
    supabase.from('sadem_personeller').select('id, ad, soyad').eq('aktif', true).order('ad').then(({ data }) => {
      if (data) setPersoneller(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.musteriId) {
      supabase.from('sadem_cihazlar').select('id, cihaz_turu, marka, model').eq('musteri_id', form.musteriId).eq('aktif', true).then(({ data }) => {
        if (data) setCihazlar(data);
      }).catch(() => {});
    } else {
      setCihazlar([]);
    }
  }, [form.musteriId]);

  const resetForm = () => {
    setForm({
      musteriId: '', cihazId: '', personelId: '',
      tarih: new Date().toISOString().split('T')[0],
      saat: '09:00',
      islemTuru: 'Filtre Bakımı – Dörtlü',
      digerAciklama: '',
      servisNotu: '',
      alinanUcret: '',
      odemeTipi: 'Nakit',
      birSonrakiBakim: '',
    });
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.musteriId) return;
    setSaving(true);
    try {
      await createServisKaydi({
        musteri_id: form.musteriId,
        cihaz_id: form.cihazId || null,
        personel_id: form.personelId || null,
        servis_tarihi: form.tarih,
        servis_saati: form.saat,
        islem_turu: form.islemTuru,
        diger_aciklama: form.islemTuru === 'Diğer' ? form.digerAciklama : '',
        servis_notu: form.servisNotu,
        alinan_ucret: form.alinanUcret ? Number(form.alinanUcret) : 0,
        odeme_tipi: form.odemeTipi,
        bir_sonraki_bakim_tarihi: form.birSonrakiBakim || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      resetForm();
      refetch();
    } catch (err: any) {
      alert('Kayıt oluşturulamadı. Lütfen tekrar deneyiniz.');
    } finally {
      setSaving(false);
    }
  };

  const handleDurumSaved = () => {
    setDurumRecord(null);
    refetch();
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteServisKaydi(id);
      setDeleteConfirm(null);
      refetch();
    } catch {
      alert('Bu işlem için yetkiniz bulunmuyor.');
    }
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-200 bg-white';
  const saatler = ['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00','17:30','18:00'];

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Servis Kayıtları</h2>
          <p className="text-sm text-gray-500 mt-0.5">Tüm servis kayıtlarını görüntüleyin ve yönetin</p>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-add-line"></i> Yeni Servis
        </button>
      </div>

      {/* Filtre + Arama */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative w-full sm:w-72">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-sky-400 bg-white"
            placeholder="Müşteri adı, telefon veya işlem ara..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setFilterDurum('tumu'); setPage(1); }}
            className={`px-3 py-2 rounded-xl text-xs font-semibold border cursor-pointer whitespace-nowrap transition-all
              ${filterDurum === 'tumu' ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'}`}>
            Tümü ({services.length > 0 ? durumCounts ? Object.values(durumCounts).reduce((a,b)=>a+b,0) : '...' : '0'})
          </button>
          {SERVIS_DURUMLARI.map(d => (
            <button key={d.value} onClick={() => { setFilterDurum(d.value); setPage(1); }}
              className={`px-3 py-2 rounded-xl text-xs font-semibold border cursor-pointer whitespace-nowrap transition-all
                ${filterDurum === d.value ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'}`}>
              {d.label} ({durumCounts?.[d.value] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* Yükleme / Hata */}
      {loading && (
        <div className="text-center py-16">
          <i className="ri-loader-4-line animate-spin text-3xl text-sky-500"></i>
          <p className="text-sm text-gray-500 mt-3">Servis kayıtları yükleniyor...</p>
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <i className="ri-error-warning-line text-red-500 text-xl"></i>
          </div>
          <p className="text-sm text-red-700 font-medium">Kayıtlar yüklenemedi.</p>
          <p className="text-xs text-red-500 mt-1">{error}</p>
          <button onClick={refetch} className="mt-3 text-sm text-red-600 font-semibold hover:text-red-800 cursor-pointer">Tekrar Dene</button>
        </div>
      )}

      {/* Liste */}
      {!loading && !error && (
        <>
          {services.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-tools-line text-2xl text-slate-400"></i>
              </div>
              <p className="text-sm text-gray-500 font-medium">
                {search || filterDurum !== 'tumu' ? 'Seçilen filtrelere uygun kayıt bulunamadı.' : 'Henüz servis kaydı bulunmuyor.'}
              </p>
              {!search && filterDurum === 'tumu' && (
                <button onClick={() => setShowForm(true)} className="mt-3 text-sky-600 text-sm font-semibold cursor-pointer hover:text-sky-800">
                  İlk Servis Kaydını Oluştur
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {services.map(s => {
                const durum = SERVIS_DURUMLARI.find(d => d.value === s.servis_durumu);
                const odeme = ODEME_DURUMLARI.find(d => d.value === s.odeme_durumu);
                return (
                  <div key={s.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:border-sky-200 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold text-gray-800">{s.musteri_ad} {s.musteri_soyad}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${islemRenk[s.islem_turu] || 'bg-gray-100 text-gray-600'}`}>{s.islem_turu}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${durum?.color || 'bg-gray-100 text-gray-600'}`}>{durum?.label || s.servis_durumu}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {s.servis_tarihi ? new Date(s.servis_tarihi).toLocaleDateString('tr-TR') : '-'} • {s.servis_saati || '-'}
                          {s.personel_ad && <span className="ml-2">• {s.personel_ad} {s.personel_soyad}</span>}
                        </p>
                        {s.servis_notu && <p className="text-xs text-gray-500 mt-1.5 italic truncate">{s.servis_notu}</p>}
                      </div>
                      <div className="text-right flex-shrink-0 flex flex-col items-end gap-1.5">
                        {s.alinan_ucret > 0 && (
                          <>
                            <p className="text-base font-bold text-emerald-600">₺{s.alinan_ucret.toLocaleString('tr-TR')}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${odeme?.color || 'bg-gray-100 text-gray-600'}`}>{odeme?.label || s.odeme_durumu}</span>
                          </>
                        )}
                        <div className="flex gap-1.5 mt-1">
                          <button onClick={() => setPrintRecord(s)} className="text-xs text-sky-600 hover:text-sky-800 cursor-pointer font-medium whitespace-nowrap flex items-center gap-0.5">
                            <i className="ri-printer-line"></i> Fiş
                          </button>
                          {(isAdmin || isOffice) && (
                            <button onClick={() => setDurumRecord(s)} className="text-xs text-amber-600 hover:text-amber-800 cursor-pointer font-medium whitespace-nowrap flex items-center gap-0.5">
                              <i className="ri-edit-line"></i> Güncelle
                            </button>
                          )}
                          {isAdmin && (
                            <button onClick={() => setDeleteConfirm(s.id)} className="text-xs text-red-500 hover:text-red-700 cursor-pointer font-medium whitespace-nowrap flex items-center gap-0.5">
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Sayfalama */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-gray-500">{totalCount} kayıttan {(page - 1) * 15 + 1}-{Math.min(page * 15, totalCount)} arası</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed text-gray-600 text-sm">
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-semibold cursor-pointer transition-colors
                      ${p === page ? 'bg-sky-600 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                    {p}
                  </button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed text-gray-600 text-sm">
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Yeni Servis Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="text-base font-bold text-gray-800">Yeni Servis Kaydı</h3>
              <button onClick={resetForm} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {saved && (
              <div className="mx-6 mt-4 flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 rounded-xl">
                <i className="ri-checkbox-circle-line"></i>
                <span>Servis kaydedildi!</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Müşteri *</label>
                <select required className={inputCls} value={form.musteriId} onChange={e => setForm(p => ({ ...p, musteriId: e.target.value, cihazId: '' }))}>
                  <option value="">Müşteri seçiniz...</option>
                  {musteriler.map(m => <option key={m.id} value={m.id}>{m.ad} {m.soyad} — {m.telefon}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Cihaz</label>
                  <select className={inputCls} value={form.cihazId} onChange={e => setForm(p => ({ ...p, cihazId: e.target.value }))}>
                    <option value="">Seçiniz...</option>
                    {cihazlar.map(c => <option key={c.id} value={c.id}>{c.cihaz_turu} — {c.marka} {c.model}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Personel</label>
                  <select className={inputCls} value={form.personelId} onChange={e => setForm(p => ({ ...p, personelId: e.target.value }))}>
                    <option value="">Atama yok...</option>
                    {personeller.map(p => <option key={p.id} value={p.id}>{p.ad} {p.soyad}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Tarih *</label>
                  <input required type="date" className={inputCls} value={form.tarih} onChange={e => setForm(p => ({ ...p, tarih: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Saat</label>
                  <select className={inputCls} value={form.saat} onChange={e => setForm(p => ({ ...p, saat: e.target.value }))}>
                    {saatler.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">İşlem Türü *</label>
                <div className="grid grid-cols-2 gap-2">
                  {ISLEM_TURLERI.map(t => (
                    <button key={t} type="button" onClick={() => setForm(p => ({ ...p, islemTuru: t }))}
                      className={`text-xs font-semibold py-2.5 px-3 rounded-lg border transition-all cursor-pointer text-left whitespace-nowrap
                        ${form.islemTuru === t ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              {form.islemTuru === 'Diğer' && (
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Diğer İşlem Açıklaması *</label>
                  <input required type="text" className={inputCls} value={form.digerAciklama} onChange={e => setForm(p => ({ ...p, digerAciklama: e.target.value }))} placeholder="İşlemi açıklayın..." />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Alınan Ücret (₺)</label>
                  <input type="number" min="0" className={inputCls} value={form.alinanUcret} onChange={e => setForm(p => ({ ...p, alinanUcret: e.target.value }))} placeholder="0" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ödeme Tipi</label>
                  <select className={inputCls} value={form.odemeTipi} onChange={e => setForm(p => ({ ...p, odemeTipi: e.target.value }))}>
                    {ODEME_TIPLERI.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Bir Sonraki Bakım Tarihi</label>
                <input type="date" className={inputCls} value={form.birSonrakiBakim} onChange={e => setForm(p => ({ ...p, birSonrakiBakim: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Servis Notu</label>
                <textarea className={`${inputCls} resize-none`} rows={2} value={form.servisNotu} onChange={e => setForm(p => ({ ...p, servisNotu: e.target.value }))} placeholder="Ek notlar..." maxLength={500} />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={resetForm} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap transition-colors disabled:opacity-50">
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Durum Güncelleme Modal */}
      {durumRecord && <DurumModal record={durumRecord} onClose={() => setDurumRecord(null)} onSaved={handleDurumSaved} />}

      {/* Yazdırma Modal */}
      {printRecord && <PrintModal record={printRecord} firmaAdi={firmaAdi} onClose={() => setPrintRecord(null)} />}

      {/* Silme Onay */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-gray-800 text-center">Servis Kaydı Silinsin mi?</h3>
            <p className="text-sm text-gray-500 text-center mt-1">Bu işlem geri alınamaz. Kayıt pasife alınacaktır.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 cursor-pointer text-sm whitespace-nowrap">İptal</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap">Evet, Sil</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}